import { ref, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useUserStore } from '@/stores/userStore'
import { useApplicationStore } from '@/stores/applicationStore'

import DataTransferService from '@/services/dataTransferService'

import {
	clientStorageLocal,
	LOCAL_STORAGE_AUTH_ACCESS_TOKEN_KEY,
	LOCAL_STORAGE_AUTH_REFRESH_TOKEN_KEY,
	LOCAL_STORAGE_LAST_SELECTED_COMPANY_ID,
} from '@ao/utils'
import { setExtraErrors } from '@/helpers/utilsHelper'

const dataTransferService = DataTransferService()

export default function useAuth() {
	const router = useRouter()
	const route = useRoute()

	const { t: $t } = useI18n()

	const userStore = useUserStore()
	const {
		currentUser,
	} = storeToRefs(userStore)

	const { addSuccessNotify, addErrorNotify } = useApplicationStore()

	const isCompanyDefined = ref(false)

	const loginState = reactive({
		loading: false,
		extraErrors: {},
		reset: false, // FIXME: вроде не используется
	})

	const registerState = reactive({
		loading: false,
		extraErrors: {},
		reset: false, // FIXME: вроде не используется
	})

	const resendingState = reactive({
		loading: false,
		nextEviableIn: null,
	})

	const saveAuthCredentials = ({access, refresh}, isRemember) => {
		return new Promise((resolve) => {
			if (isRemember) {
				// FIXME: если пользователь не выбрал remember то сохранять в sessionStorage
				clientStorageLocal.add(LOCAL_STORAGE_AUTH_ACCESS_TOKEN_KEY, access)
				clientStorageLocal.add(LOCAL_STORAGE_AUTH_REFRESH_TOKEN_KEY, refresh)
			}
			resolve(true)
		})
	}

	const register = (payload) => {
		return new Promise((resolve, reject) => {
			registerState.loading = true
			dataTransferService.auth.registerUser(payload)
				.then((request) => {
					const { access, refresh } = request.token

					saveAuthCredentials({access, refresh}, true)

					registerState.loading = false
					resolve({
						authAccessToken: access,
						authRefreshToken: refresh,
					})
				})
				.catch((err) => {
					// eslint-disable-next-line no-console
					console.error(err)
					addErrorNotify({
						text: $t('Something went wrong. Contact Support.'),
						timeout: 5000,
					})

					setExtraErrors(registerState, err)
					registerState.loading = false
					reject(err)
				})
		})
	}

	const login = (payload) => {
		return new Promise((resolve, reject) => {
			loginState.loading = true
			dataTransferService.auth.loginUser(payload)
				.then((request) => {
					const { access, refresh } = request

					saveAuthCredentials({access, refresh}, payload.remember)

					loginState.loading = false
					resolve({
						authAccessToken: access,
						authRefreshToken: refresh,
					})
				})
				.catch((err) => {
					// eslint-disable-next-line no-console
					console.error(err)
					addErrorNotify({
						text: $t('Something went wrong. Contact Support.'),
						timeout: 5000,
					})

					setExtraErrors(loginState, err)
					loginState.loading = false
					reject(err)
				})
		})
	}

	const logoutUser = () => {
		// RISK(RISK-004)[S2/безопасность]: при логауте не очищается кэш IndexedDB
		// (indexedDB.deleteDatabase не вызывается нигде) — данные компании переживают смену
		// пользователя на общем устройстве. Решение: очистка БД кэша здесь и на смене компании.
		// Подробнее: docs/audit/04_risks_register.md (RISK-004)
		clientStorageLocal.remove(LOCAL_STORAGE_AUTH_ACCESS_TOKEN_KEY)
		clientStorageLocal.remove(LOCAL_STORAGE_AUTH_REFRESH_TOKEN_KEY)

		userStore.$reset()
	}

	const resendConfirmationEmail = async () => {
		try {
			resendingState.loading = true

			await dataTransferService.auth.resendEmailConfirmation({
				urlParams: {
					id: currentUser.id,
				},
			})

			resendingState.loading = false
			addSuccessNotify({
				text: $t('Confirmation email sent to email') + ' ' + currentUser.email,
				timeout: 5000,
			})
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error)

			resendingState.loading = false
			addErrorNotify({
				text: $t('Something went wrong. Contact Support.'),
				timeout: 5000,
			})
		}
	}

	const getCompanyIdFromRoute = () => {
		// console.log('getCompanyIdFromRoute route.params.companyId', route, route.name, route.params, route.params.companyId)
		// setTimeout(() => {
		// 	console.log('-=250=- getCompanyIdFromRoute route.params.companyId', route, route.name, route.params, route.params.companyId)
		// }, 250)
		// setTimeout(() => {
		// 	console.log('-=500=- getCompanyIdFromRoute route.params.companyId', route, route.name, route.params, route.params.companyId)
		// }, 500)
		return new Promise((resolve) => {
			router.isReady().then(() => {
				if (route.params.companyId) {
					resolve(parseInt(route.params.companyId))
				} else {
					resolve(null)
				}
			})
		})
	}

	const getCompanyIdFromLocalStore = () => {
		return new Promise((resolve) => {
			let companyIdFromLs = clientStorageLocal.get(LOCAL_STORAGE_LAST_SELECTED_COMPANY_ID)
			try {
				if (companyIdFromLs) companyIdFromLs = parseInt(companyIdFromLs)
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error)
			}
			if (companyIdFromLs) {
				resolve(companyIdFromLs)
			} else {
				resolve(null)
			}
		})
	}

	const getDefinedCompanyId = async (availableCompanies = []) => {
		// console.log('getDefinedCompanyId', availableCompanies)

		const companyIdFromRoute = await getCompanyIdFromRoute()
		// console.log('companyIdFromRoute', companyIdFromRoute)
		if (companyIdFromRoute) return companyIdFromRoute

		const companyIdFromLocalStore = await getCompanyIdFromLocalStore()
		if (companyIdFromLocalStore) return companyIdFromLocalStore

		if (availableCompanies.length) {
			return availableCompanies[0].id
		}

		return null
	}

	return {
		loginState,
		registerState,
		resendingState,
		register,
		login,
		logoutUser,
		resendConfirmationEmail,
		isCompanyDefined,
		getDefinedCompanyId,
	}
}
